import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  QrCodeScanner as QRIcon,
  Description as CertificateIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenIcon,
  Business as SupplierIcon,
  Category as MaterialIcon,
  Numbers as BatchIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { BarcodeScanner } from '@capacitor-mlkit/barcode-scanning';
import { CapacitorNfc } from '@capgo/capacitor-nfc';
import { getApiUrl } from '../utils/apiUrl';

const MobileMaterialCertificate = () => {
  const API_BASE = getApiUrl();
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [certificate, setCertificate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [nfcSupported, setNfcSupported] = useState(null);
  const [nfcScanning, setNfcScanning] = useState(false);
  const [nfcWriting, setNfcWriting] = useState(false);
  const [nfcSuccess, setNfcSuccess] = useState('');

  const getToken = () => localStorage.getItem('token');

  // Check NFC support on mount
  React.useEffect(() => {
    const checkNfc = async () => {
      try {
        const { supported } = await CapacitorNfc.isSupported();
        setNfcSupported(supported);
      } catch (e) {
        setNfcSupported(false);
      }
    };
    checkNfc();
  }, []);

  // Start NFC read scan
  const startNfcScan = async () => {
    try {
      setError('');
      setNfcSuccess('');
      setCertificate(null);
      setNfcScanning(true);

      const { status } = await CapacitorNfc.getStatus();
      if (status === 'NFC_DISABLED') {
        setError('NFC er deaktivert. Aktiver NFC i innstillingene.');
        setNfcScanning(false);
        try { await CapacitorNfc.showSettings(); } catch (e) {}
        return;
      }

      let nfcListener = null;
      let timeoutId = null;

      await CapacitorNfc.startScanning({
        invalidateAfterFirstRead: true,
        alertMessage: 'Hold telefonen mot NFC-taggen på materialet',
      });

      nfcListener = await CapacitorNfc.addListener('nfcEvent', async (event) => {
        if (timeoutId) clearTimeout(timeoutId);
        setNfcScanning(false);
        try {
          if (nfcListener) await nfcListener.remove();
          await CapacitorNfc.stopScanning();
        } catch (e) {}

        const records = event.tag?.ndefMessage;
        if (records && records.length > 0) {
          const payload = records[0].payload;
          if (payload && payload.length > 0) {
            const langLen = payload[0] & 0x3f;
            const textBytes = payload.slice(1 + langLen);
            const text = new TextDecoder().decode(new Uint8Array(textBytes));
            console.log('NFC tag read:', text);
            await handleCertificateScanned(text);
          } else {
            setError('NFC-taggen inneholder ingen data');
          }
        } else {
          setError('Ingen NDEF-data funnet på taggen');
        }
      });

      timeoutId = setTimeout(async () => {
        setNfcScanning(false);
        try {
          if (nfcListener) await nfcListener.remove();
          await CapacitorNfc.stopScanning();
        } catch (e) {}
      }, 30000);

    } catch (err) {
      console.error('NFC scan error:', err);
      setNfcScanning(false);
      if (err.message && err.message.includes('cancel')) return;
      setError(`NFC-feil: ${err.message || 'Ukjent feil'}`);
    }
  };

  // Write certificate data to NFC tag
  const writeNfcTag = async (certNumber) => {
    try {
      setNfcWriting(true);
      setError('');
      setNfcSuccess('');

      const { status } = await CapacitorNfc.getStatus();
      if (status === 'NFC_DISABLED') {
        setError('NFC er deaktivert. Aktiver NFC i innstillingene.');
        setNfcWriting(false);
        try { await CapacitorNfc.showSettings(); } catch (e) {}
        return;
      }

      let nfcListener = null;
      let timeoutId = null;

      // Android reader flags WITHOUT FLAG_READER_SKIP_NDEF_CHECK (0x80)
      // so that Android properly identifies NDEF tech for writing.
      // NFC_A(1) | NFC_B(2) | NFC_F(4) | NFC_V(8) | NO_PLATFORM_SOUNDS(16) = 31
      const WRITE_READER_FLAGS = 1 | 2 | 4 | 8 | 16;

      await CapacitorNfc.startScanning({
        invalidateAfterFirstRead: false,
        androidReaderModeFlags: WRITE_READER_FLAGS,
        alertMessage: 'Hold telefonen mot NFC-taggen du vil skrive til',
      });

      nfcListener = await CapacitorNfc.addListener('nfcEvent', async (event) => {
        if (timeoutId) clearTimeout(timeoutId);
        try {
          // Log tag info for debugging
          const tagType = event.tag?.techTypes || event.tag?.type || 'unknown';
          console.log('NFC tag detected for write, type:', tagType, 'tag:', JSON.stringify(event.tag));

          const encoder = new TextEncoder();
          const langBytes = Array.from(encoder.encode('en'));
          const textBytes = Array.from(encoder.encode(certNumber));
          const payload = [langBytes.length & 0x3f, ...langBytes, ...textBytes];

          await CapacitorNfc.write({
            allowFormat: true,
            records: [{
              tnf: 0x01,
              type: [0x54],
              id: [],
              payload,
            }],
          });

          setNfcSuccess(`NFC-tag skrevet med sertifikat: ${certNumber}`);
        } catch (writeErr) {
          console.error('NFC write error:', writeErr);
          const msg = (writeErr.message || '').toLowerCase();
          if (msg.includes('ndef') || msg.includes('format')) {
            setError('Kunne ikke skrive til taggen. Hold telefonen stille mot taggen og prøv igjen. Hvis feilen gjentar seg, kan taggen være inkompatibel.');
          } else if (msg.includes('read-only') || msg.includes('readonly')) {
            setError('NFC-taggen er skrivebeskyttet og kan ikke skrives til.');
          } else if (msg.includes('too small') || msg.includes('size') || msg.includes('capacity')) {
            setError('NFC-taggen har for lite minne. Bruk en NTAG215 eller NTAG216.');
          } else {
            setError(`Kunne ikke skrive til NFC-tag: ${writeErr.message || 'Ukjent feil'}`);
          }
        } finally {
          setNfcWriting(false);
          try {
            if (nfcListener) await nfcListener.remove();
            await CapacitorNfc.stopScanning();
          } catch (e) {}
        }
      });

      timeoutId = setTimeout(async () => {
        setNfcWriting(false);
        try {
          if (nfcListener) await nfcListener.remove();
          await CapacitorNfc.stopScanning();
        } catch (e) {}
        setError('Tidsavbrudd - ingen NFC-tag oppdaget');
      }, 30000);

    } catch (err) {
      console.error('NFC write error:', err);
      setNfcWriting(false);
      if (err.message && err.message.includes('cancel')) return;
      setError(`NFC-feil: ${err.message || 'Ukjent feil'}`);
    }
  };

  // Cancel NFC scanning
  const cancelNfcScan = async () => {
    try {
      await CapacitorNfc.stopScanning();
    } catch (e) {}
    setNfcScanning(false);
    setNfcWriting(false);
  };

  // Request permissions
  const requestPermissions = async () => {
    const { camera } = await BarcodeScanner.requestPermissions();
    return camera === 'granted' || camera === 'limited';
  };

  // Start QR scanning
  const startScan = async () => {
    try {
      setError('');
      setCertificate(null);

      if (!BarcodeScanner) {
        setError('Barcode scanner er ikke tilgjengelig på denne enheten');
        return;
      }

      const granted = await requestPermissions();
      if (!granted) {
        setError('Kamera-tillatelse er nødvendig for å skanne QR-koder.');
        return;
      }

      setScanning(true);

      const scanPromise = BarcodeScanner.scan();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Scan timeout')), 30000)
      );

      let result;
      try {
        result = await Promise.race([scanPromise, timeoutPromise]);
      } catch (timeoutErr) {
        try {
          await BarcodeScanner.stopScan();
        } catch (e) {
          console.log('Stop scan error:', e);
        }
        setScanning(false);
        return;
      }

      const { barcodes } = result;
      setScanning(false);

      if (barcodes && barcodes.length > 0) {
        const qrData = barcodes[0].rawValue;
        console.log('QR Code scanned:', qrData);
        await handleCertificateScanned(qrData);
      } else {
        setError('Ingen QR-kode funnet');
      }
    } catch (err) {
      console.error('Scan error:', err);
      setScanning(false);
      
      if (err.message && err.message.includes('cancelled')) {
        return;
      }
      
      setError(`Kunne ikke skanne: ${err.message || 'Ukjent feil'}`);
    }
  };

  // Handle certificate QR scanned
  const handleCertificateScanned = async (qrContent) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${API_BASE}/steel/certificates/search-qr/${encodeURIComponent(qrContent)}`,
        {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Sertifikat ikke funnet');
      }

      const cert = await response.json();
      setCertificate(cert);
    } catch (err) {
      console.error('Certificate fetch error:', err);
      setError(err.message || 'Kunne ikke finne sertifikat');
    } finally {
      setLoading(false);
    }
  };

  // Search certificates
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    setError('');
    setSearchResults([]);

    try {
      const response = await fetch(
        `${API_BASE}/steel/certificates/search?query=${encodeURIComponent(searchQuery)}`,
        {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        }
      );

      if (!response.ok) {
        throw new Error('Søk feilet');
      }

      const results = await response.json();
      setSearchResults(results);
      
      if (results.length === 0) {
        setError('Ingen sertifikater funnet');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Kunne ikke søke etter sertifikater');
    } finally {
      setSearching(false);
    }
  };

  // Select certificate from search results
  const handleSelectCertificate = (cert) => {
    setCertificate(cert);
    setSearchResults([]);
    setSearchQuery('');
  };

  // Reset
  const handleReset = () => {
    setCertificate(null);
    setError('');
    setSearchQuery('');
    setSearchResults([]);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nb-NO');
  };

  return (
    <Box>
      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* NFC success display */}
      {nfcSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNfcSuccess('')}>
          {nfcSuccess}
        </Alert>
      )}

      {/* NFC scanning state */}
      {(nfcScanning || nfcWriting) && (
        <Card sx={{ mb: 2, bgcolor: nfcWriting ? '#f59e0b' : '#8b5cf6', color: 'white' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: 60, mb: 1 }}>📡</Typography>
            <Typography variant="h6" gutterBottom>
              {nfcWriting ? 'Skriver til NFC-tag...' : 'Skanner NFC...'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {nfcWriting ? 'Hold telefonen mot NFC-taggen du vil skrive til' : 'Hold telefonen mot NFC-taggen på materialet'}
            </Typography>
            <Button
              variant="outlined"
              sx={{ color: 'white', borderColor: 'white' }}
              onClick={cancelNfcScan}
            >
              Avbryt
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Scanning state */}
      {scanning && (
        <Card sx={{ mb: 2, bgcolor: 'primary.main', color: 'white' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <QRIcon sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Skanner...
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Skann QR-kode på material sertifikat
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 2, opacity: 0.9 }}>
              💡 Trykk på enhetens tilbake-knapp for å avbryte
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Main view - scan and search options */}
      {!scanning && !loading && !certificate && (
        <>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CertificateIcon color="primary" />
                Material Sertifikater
              </Typography>
              
              {/* QR Scan button */}
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                startIcon={<QRIcon />}
                onClick={startScan}
                sx={{ py: 1.5, mb: 1 }}
              >
                📷 Skann Sertifikat QR
              </Button>

              {/* NFC Scan button */}
              {nfcSupported && (
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={startNfcScan}
                  disabled={nfcScanning}
                  sx={{ 
                    py: 1.5, mb: 2,
                    bgcolor: '#8b5cf6',
                    '&:hover': { bgcolor: '#7c3aed' }
                  }}
                >
                  📡 Skann NFC-tag
                </Button>
              )}

              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" color="text.secondary">eller</Typography>
              </Divider>

              {/* Manual search */}
              <TextField
                fullWidth
                placeholder="Søk på sertifikatnr, material, leverandør..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => { setSearchQuery(''); setSearchResults([]); }}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 1 }}
              />
              <Button
                fullWidth
                variant="outlined"
                startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
                onClick={handleSearch}
                disabled={!searchQuery.trim() || searching}
              >
                {searching ? 'Søker...' : 'Søk'}
              </Button>
            </CardContent>
          </Card>

          {/* Search results */}
          {searchResults.length > 0 && (
            <Card>
              <CardContent sx={{ pb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Søkeresultat ({searchResults.length})
                </Typography>
              </CardContent>
              <List dense>
                {searchResults.map((cert, idx) => (
                  <ListItemButton 
                    key={cert.id || idx} 
                    onClick={() => handleSelectCertificate(cert)}
                    sx={{ borderBottom: idx < searchResults.length - 1 ? '1px solid #eee' : 'none' }}
                  >
                    <CertificateIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <ListItemText
                      primary={cert.certificateNumber}
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            {cert.material || '-'} • {cert.supplier || '-'}
                          </Typography>
                          {cert.batch && (
                            <Typography variant="caption" color="text.secondary">
                              Batch: {cert.batch}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Card>
          )}
        </>
      )}

      {/* Certificate display */}
      {certificate && !loading && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CertificateIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  {certificate.certificateNumber}
                </Typography>
              </Box>
              <IconButton onClick={handleReset} color="primary">
                <RefreshIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <List dense>
              <ListItem>
                <MaterialIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <ListItemText 
                  primary="Material"
                  secondary={certificate.material || '-'}
                />
              </ListItem>

              <ListItem>
                <SupplierIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <ListItemText 
                  primary="Leverandør"
                  secondary={certificate.supplier || '-'}
                />
              </ListItem>

              <ListItem>
                <BatchIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <ListItemText 
                  primary="Batch / Charge"
                  secondary={`${certificate.batch || '-'} / ${certificate.charge || '-'}`}
                />
              </ListItem>
            </List>

            {/* Additional info */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Dimensjon:</strong> {certificate.dimension || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Standard:</strong> {certificate.standard || '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Opprettet:</strong> {formatDate(certificate.createdAt)}
              </Typography>
            </Box>

            {/* View PDF button */}
            {certificate.storagePath && (
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={<OpenIcon />}
                sx={{ mt: 2 }}
                onClick={() => {
                  const downloadUrl = `${API_BASE}/steel/certificates/download/${certificate.id}?token=${getToken()}`;
                  window.open(downloadUrl, '_blank');
                }}
              >
                📄 Åpne {certificate.originalName || 'Sertifikat'}
              </Button>
            )}

            {/* NFC write button */}
            {nfcSupported && certificate.certificateNumber && (
              <Button
                fullWidth
                variant="outlined"
                sx={{ mt: 2, borderColor: '#8b5cf6', color: '#8b5cf6', '&:hover': { borderColor: '#7c3aed', bgcolor: '#f5f3ff' } }}
                onClick={() => writeNfcTag(certificate.certificateNumber)}
                disabled={nfcWriting}
              >
                📡 Skriv til NFC-tag
              </Button>
            )}

            {/* Scan again buttons */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<QRIcon />}
                onClick={() => {
                  handleReset();
                  startScan();
                }}
              >
                Skann QR
              </Button>
              {nfcSupported && (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    handleReset();
                    startNfcScan();
                  }}
                  sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}
                >
                  📡 NFC
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MobileMaterialCertificate;
